import React, { useEffect, useRef, useState } from "react";
import { Select, Input, Progress } from "antd";
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  RotateRightOutlined,
  SwapOutlined,
  UndoOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { EVALUATION_PATH as PATH } from "../../../redux/utils";
import AppToast from "../../../components/toasts/toastDark";
import logoEvaluation from "../../../assets/img/logo_evaluation.webp";

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;

const { TextArea } = Input;

const CLASSIFICATIONS = [
  { value: "", label: "Select an option" },
  { value: "NF", label: "Normal Fundus" },
  { value: "TF", label: "Tessellated Fundus" },
  { value: "CA", label: "Choroidal Diffuse Atrophy" },
  { value: "PA", label: "Patchy Atrophy" },
];

// Practice-only images, served as static files from the evaluation backend's
// /uploads mount. They are never listed via /evaluation/images and never
// touch the retinal_images table — completely separate from the real dataset.
const DRYRUN_IMAGE_NAMES = [
  "N0138.jpg",
  "P0012.jpg",
  "P0014.jpg",
  "P0015.jpg",
  "P0043.jpg",
  "P0047.jpg",
  "P0199.jpg",
  "P0200.jpg",
  "P0201.jpg",
  "P0202.jpg",
  "P0203.jpg",
  "P0204.jpg",
  "P0205.jpg",
  "P0207.jpg",
  "P0208.jpg",
];

const DRYRUN_STORAGE_KEY = "evaluation_dryrun_answers";

function loadStoredAnswers() {
  try {
    const raw = window.localStorage.getItem(DRYRUN_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function storeAnswers(answers) {
  try {
    window.localStorage.setItem(DRYRUN_STORAGE_KEY, JSON.stringify(answers));
  } catch {
    // ignore storage errors, this is practice-only data
  }
}

export default function EvaluationDryRunPage() {
  const [images] = useState(() => DRYRUN_IMAGE_NAMES.map((name) => ({ name })));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [classification, setClassification] = useState("");
  const [notes, setNotes] = useState("");
  const [answers, setAnswers] = useState(() => loadStoredAnswers());
  const [imgLoaded, setImgLoaded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const [toastMsg, setToastMsg] = useState("");
  const [showToast, setShowToast] = useState(false);

  const toast = (msg) => { setToastMsg(msg); setShowToast(true); };

  const total = images.length;
  const current = images[currentIndex];
  const isDone = total > 0 && currentIndex >= total;

  useEffect(() => {
    const img = images[currentIndex];
    const saved = img ? answers[img.name] : undefined;
    setClassification(saved ? saved.classification : "");
    setNotes(saved ? saved.notes : "");
    setImgLoaded(false);
    setZoom(1);
    setRotation(0);
    setFlipH(false);
    setPan({ x: 0, y: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, images]);

  const saveCurrentAnswer = () => {
    if (!current) return;
    const next = {
      ...answers,
      [current.name]: { classification, notes },
    };
    setAnswers(next);
    storeAnswers(next);
  };

  const handleNext = () => {
    if (!classification) {
      toast("Select a classification before continuing");
      return;
    }
    if (!current) return;
    saveCurrentAnswer();
    setCurrentIndex((i) => i + 1);
  };

  const handlePrevious = () => {
    if (currentIndex === 0) return;
    saveCurrentAnswer();
    setCurrentIndex((i) => i - 1);
  };

  const zoomIn = () => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () =>
    setZoom((z) => {
      const next = Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2));
      if (next === ZOOM_MIN) setPan({ x: 0, y: 0 });
      return next;
    });
  const rotate = () => setRotation((r) => (r + 90) % 360);
  const toggleFlip = () => setFlipH((f) => !f);
  const resetView = () => {
    setZoom(1);
    setRotation(0);
    setFlipH(false);
    setPan({ x: 0, y: 0 });
  };

  const handleImageMouseDown = (e) => {
    if (zoom <= ZOOM_MIN) return;
    setDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  };
  const handleImageMouseMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    setPan({ x: dragStartRef.current.panX + dx, y: dragStartRef.current.panY + dy });
  };
  const handleImageMouseUp = () => setDragging(false);

  const handleDownload = async () => {
    const url = `${PATH}/uploads/evaluation/dryrun_retinal_images/${current.name}`;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = current.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  if (total === 0) {
    return (
      <div className="evaluation-card">
        <h1 className="evaluation-title">Dry run</h1>
        <p className="evaluation-subtitle">No practice images are available.</p>
      </div>
    );
  }

  if (isDone) {
    return (
      <div className="evaluation-card">
        <h1 className="evaluation-title">Dry run complete</h1>
        <p className="evaluation-subtitle">
          You practiced with all {total} sample images. Nothing was saved to the
          database — this was just a rehearsal.
        </p>
        <button
          type="button"
          className="evaluation-btn evaluation-btn-secondary"
          onClick={() => setCurrentIndex(total - 1)}
        >
          ← Back
        </button>
      </div>
    );
  }

  const percent = Math.round((currentIndex / total) * 100);

  return (
    <div className="evaluation-eval-screen">
      <div className="evaluation-eval-progress">
        <span className="evaluation-eval-progress-label">
          Progress: {currentIndex + 1} / {total}
        </span>
        <Progress
          percent={percent}
          showInfo={false}
          strokeColor="var(--eh-primary-btn, #01369e)"
          trailColor="#232b35"
          className="evaluation-progress-bar"
        />
      </div>

      <div className="evaluation-eval-body">
        <div className={`evaluation-eval-image-frame${imgLoaded ? "" : " is-loading"}`}>
          <div className="evaluation-eval-toolbar">
            <button type="button" onClick={zoomOut} disabled={zoom <= ZOOM_MIN} title="Zoom out">
              <ZoomOutOutlined />
            </button>
            <span className="evaluation-eval-zoom-label">{Math.round(zoom * 100)}%</span>
            <button type="button" onClick={zoomIn} disabled={zoom >= ZOOM_MAX} title="Zoom in">
              <ZoomInOutlined />
            </button>
            <span className="evaluation-eval-toolbar-sep" />
            <button type="button" onClick={rotate} title="Rotate 90°">
              <RotateRightOutlined />
            </button>
            <button
              type="button"
              className={flipH ? "is-active" : undefined}
              onClick={toggleFlip}
              title="Flip horizontal"
            >
              <SwapOutlined />
            </button>
            <span className="evaluation-eval-toolbar-sep" />
            <button type="button" onClick={resetView} title="Reset view">
              <UndoOutlined />
            </button>
            <button type="button" onClick={handleDownload} title="Download image">
              <DownloadOutlined />
            </button>
          </div>

          <div
            className="evaluation-eval-image-stage"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              cursor: zoom > ZOOM_MIN ? (dragging ? "grabbing" : "grab") : "default",
            }}
            onMouseDown={handleImageMouseDown}
            onMouseMove={handleImageMouseMove}
            onMouseUp={handleImageMouseUp}
            onMouseLeave={handleImageMouseUp}
          >
            <img
              key={current.name}
              src={`${PATH}/uploads/evaluation/dryrun_retinal_images/${current.name}`}
              alt="Retinal fundus (practice)"
              className="evaluation-eval-image"
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              onLoad={() => setImgLoaded(true)}
              style={{ transform: `rotate(${rotation}deg) scaleX(${flipH ? -1 : 1})` }}
            />
          </div>
        </div>

        <div className="evaluation-eval-form">
          <div className="evaluation-form-group">
            <label className="evaluation-form-label">Classification</label>
            <Select
              className="evaluation-select"
              popupClassName="evaluation-select-dropdown"
              options={CLASSIFICATIONS}
              value={classification}
              onChange={setClassification}
            />
          </div>

          <div className="evaluation-form-group">
            <label className="evaluation-form-label">Notes (optional)</label>
            <TextArea
              className="evaluation-textarea"
              placeholder="Observations about the image…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              autoSize={{ minRows: 3, maxRows: 6 }}
            />
          </div>

          <div className="evaluation-eval-actions">
            <button
              type="button"
              className="evaluation-btn evaluation-btn-secondary"
              onClick={handlePrevious}
              disabled={currentIndex === 0}
            >
              ← Back
            </button>
            <button type="button" className="evaluation-btn" onClick={handleNext}>
              Next →
            </button>
          </div>
        </div>
      </div>

      <AppToast show={showToast} setShow={setShowToast} msg={toastMsg} logo={logoEvaluation} />
    </div>
  );
}
